import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { type Campaign } from '@hooks-api/types/dataModel/campaigns'
import { useEffect, useRef, type SetStateAction } from 'react'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { useShallow } from 'zustand/react/shallow'
import { BUTLER_TAB, isButlerTab } from '../constants/butler-tab'
import { type FpB2bCartValues } from '../types'
export const CART_STORAGE_KEY = 'fpMerchantCartStorage'

type FpB2bCartState = Partial<Record<Campaign['campaign_id'], FpB2bCartValues>>

export const defaultCartValues: FpB2bCartValues = {
  butler: {
    draftName: '',
    orderId: null,
    isImported: false,
    errors: {},
    selectedTab: BUTLER_TAB.FILE,
    items: [],
    fileName: '',
  },
  cart: {
    items: [],
    addonItems: [],
    specificAddonItems: [],
  },
  merchant: {
    index: null,
    billingFirstName: '',
    billingCountry: null,
  },
  coupon: {
    manualInputCouponIds: [],
    /**
     * 常態滿額禮
     */
    selectedRegularGiveaways: [],
    /**
     * 贈品
     */
    selectedGiveaways: [],
    redeemedCodes: [],
    hasApplyManuallyRedeemedCodes: false,
    selectedClaimCouponId: null,
    // 點數兌換券僅 WP 購物車消費；FP/STM 不讀此欄位，補型別預設即可，無需 persist version migration。
    selectedExchangeVoucherRedeemCodes: [],
    selectedExchangeVoucherGiveaways: [],
    selectedManualCouponId: null,
  },
  shipping: {
    appliedShippingMethodId: null,
  },
  paymentTransaction: {
    id: null,
    status: null,
    appliedRepaymentMethodId: null,
    orderHash: null,
  },
}

const migrateMap = {
  /**
   * release 2601 新增欄位 specificAddonItems
   * 單品加價購 https://www.notion.so/shepherdtech/2513-2601-2a0a4b7afc8880d983e9e0f43b98a225?source=copy_link
   */
  2601: (persistedState: FpB2bCartState) => {
    return Object.keys(persistedState).reduce<FpB2bCartState>((acc, campaignId) => {
      acc[Number(campaignId)] = {
        ...(persistedState[Number(campaignId)] || defaultCartValues),
        cart: {
          ...(persistedState[Number(campaignId)]?.cart || defaultCartValues.cart),
          specificAddonItems: [],
        },
      }
      return acc
    }, {})
  },
  /**
   * release 2613 移除文字辨識分頁，persisted 的 butler.selectedTab 可能還停在已移除的分頁
   */
  2613: (persistedState: FpB2bCartState) => {
    return Object.keys(persistedState).reduce<FpB2bCartState>((acc, campaignId) => {
      const cartValues = persistedState[Number(campaignId)] || defaultCartValues
      acc[Number(campaignId)] = {
        ...cartValues,
        butler: {
          ...cartValues.butler,
          selectedTab: isButlerTab(cartValues.butler.selectedTab) ? cartValues.butler.selectedTab : defaultCartValues.butler.selectedTab,
        },
      }
      return acc
    }, {})
  },
}

export const useFpMerchantCartStoreBase = create<FpB2bCartState>()(
  persist(
    () => ({}),
    {
      name: CART_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      /**
       * - version 1: 2601 單品加價購，新增 cart.specificAddonItems
       * - version 2: 2613 移除文字辨識分頁，butler.selectedTab 落在已移除分頁時回預設
       */
      version: 2,
      migrate: (persistedState, version) => {
        let state = persistedState as FpB2bCartState
        if (version < 1) {
          state = migrateMap[2601](state)
        }
        if (version < 2) {
          state = migrateMap[2613](state)
        }
        return state
      },
    },
  ),
)

const registerUpdateStore = () => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return
  // 當使用者切換頁面時，重新hydrate store
  const updateStore = () => {
    useFpMerchantCartStoreBase.persist.rehydrate()
  }

  document.addEventListener('visibilitychange', updateStore)
  window.addEventListener('focus', updateStore)
}
registerUpdateStore()

export const getFpMerchantCartValues = (campaignId: number | null): FpB2bCartValues =>
  (campaignId && useFpMerchantCartStoreBase.getState()[campaignId]) || defaultCartValues

export const useFpMerchantCartSelector = <U>(campaignId: number | null, selector: (state: FpB2bCartValues) => U) => {
  return useFpMerchantCartStoreBase(useShallow(state => selector((campaignId && state[campaignId]) || defaultCartValues)))
}

export type SetFpB2bCartValuesCallback = (state: FpB2bCartValues) => void

export const useFpMerchantCartStore = (campaignId: number | null) => {
  if (!campaignId) {
    throw new Error('campaignId is required')
  }
  const cartValues = useFpMerchantCartStoreBase(state => state[campaignId] || defaultCartValues)
  const callbacks = useRef<SetFpB2bCartValuesCallback[]>([])

  const setState = useCallbackEvent((value: SetStateAction<FpB2bCartValues>, callback?: SetFpB2bCartValuesCallback) => {
    const prevState = useFpMerchantCartStoreBase.getState()[campaignId]
    useFpMerchantCartStoreBase.setState((prevState) => {
      const nextState = typeof value === 'function' ? value(prevState[campaignId] || defaultCartValues) : value
      return {
        ...prevState,
        [campaignId]: nextState,
      }
    })
    const nextState = useFpMerchantCartStoreBase.getState()[campaignId]
    if (callback) callbacks.current.push(callback)
    return { prevState, nextState }
  })

  useEffect(() => {
    if (callbacks.current) {
      callbacks.current.forEach(func => func(cartValues))

      callbacks.current = []
    }
  }, [cartValues])

  return [cartValues, setState] as const
}
